// Криейтър: пари — баланс, история, верификация с лична карта и теглене.
// Тегленето изисква: одобрен профил, верификация и баланс над минимума.

import React, { useCallback, useState } from 'react';
import { Linking, Text, View } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { useFocusEffect } from '@react-navigation/native';
import { api, API_URL, money, uploadForm } from '../../api';
import { showError, showSuccess } from '../../toast';
import { Badge, Button, Card, COLORS, Divider, Input, Label, Muted, Row, Screen, SectionLabel, Select, Title } from '../../ui';
import { getLocale, t } from '../../i18n';

const wStatus = (): Record<string, [string, string]> => ({
  REQUESTED: [t('Чака превод'), '#D97706'],
  PAID: [t('Изплатено ✓'), COLORS.success],
  REJECTED: [t('Отказано'), COLORS.danger],
});

export default function CreatorMoneyScreen() {
  const [me, setMe] = useState<any>(null);
  const [amount, setAmount] = useState('');
  const [iban, setIban] = useState('');
  const [idCardUri, setIdCardUri] = useState<string | null>(null);
  const [selfieUri, setSelfieUri] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Данни за фактура (криейтърът издава фактура на платформата)
  const [bType, setBType] = useState<'INDIVIDUAL' | 'COMPANY'>('INDIVIDUAL');
  const [bName, setBName] = useState('');
  const [bAddress, setBAddress] = useState('');
  const [bCity, setBCity] = useState('');
  const [bEgn, setBEgn] = useState('');
  const [bEik, setBEik] = useState('');
  const [bMol, setBMol] = useState('');
  const [bVat, setBVat] = useState('');
  const W_STATUS = wStatus();

  const load = useCallback(() => {
    api('/creator/me')
      .then((r) => {
        setMe(r);
        if (r.profile.iban && !iban) setIban(r.profile.iban);
        const b = r.profile.billing;
        if (b) {
          if (b.personType) setBType(b.personType);
          setBName(b.name ?? '');
          setBAddress(b.address ?? '');
          setBCity(b.city ?? '');
          setBEik(b.eik ?? '');
          setBMol(b.mol ?? '');
          setBVat(b.vat ?? '');
        }
      })
      .catch((e) => showError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useFocusEffect(load);

  if (!me) return <Screen><Muted>{t('Зареждане…')}</Muted></Screen>;
  const { profile, settings, withdrawals, ledger, invoices } = me;
  const billingComplete = profile.billing?.complete;

  const saveBilling = async () => {
    setLoading(true);
    try {
      await api('/creator/billing', {
        method: 'PUT',
        body: {
          personType: bType,
          name: bName.trim(),
          address: bAddress.trim(),
          ...(bCity.trim() ? { city: bCity.trim() } : {}),
          ...(bType === 'INDIVIDUAL' ? { egn: bEgn.trim() } : { eik: bEik.trim(), mol: bMol.trim() || undefined }),
          ...(bVat.trim() ? { vat: bVat.trim() } : {}),
        },
      });
      showSuccess(t('Данните за фактура са запазени.'));
      load();
    } catch (e: any) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const pick = async (setter: (uri: string) => void) => {
    const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ['images'], quality: 0.8 });
    if (!result.canceled && result.assets[0]) setter(result.assets[0].uri);
  };

  const sendVerification = async () => {
    if (!idCardUri || !selfieUri) return;
    setLoading(true);
    try {
      await uploadForm('/creator/verification', [
        { field: 'idCard', uri: idCardUri },
        { field: 'selfie', uri: selfieUri },
      ]);
      setIdCardUri(null);
      setSelfieUri(null);
      showSuccess(t('Документите са изпратени. Администратор ще ги прегледа.'));
      load();
    } catch (e: any) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const withdraw = async () => {
    setLoading(true);
    try {
      const cents = Math.round(Number(amount.replace(',', '.')) * 100);
      await api('/creator/withdrawals', { body: { amountCents: cents, iban: iban.trim() } });
      setAmount('');
      showSuccess(t('Заявката е изпратена. Преводът се прави ръчно по банка.'));
      load();
    } catch (e: any) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Screen>
      <Title>{t('Пари')}</Title>

      <Card>
        <Text style={{ fontSize: 30, fontWeight: '800', color: COLORS.text }}>{money(profile.balanceCents)}</Text>
        <Muted>{t('Наличен баланс · минимум за теглене {min} €', { min: (settings.minWithdrawCents / 100).toFixed(0) })}</Muted>
      </Card>

      {/* Данни за фактура — криейтърът издава фактура на платформата */}
      <SectionLabel>{t('Данни за фактура')}</SectionLabel>
      <Card>
        <Muted>
          {t('За услугата „създаване на видео съдържание" ти издаваш фактура на платформата. Попълни данните — те са задължителни преди верификация и теглене.')}{' '}
          {billingComplete ? '' : t('⚠️ Още не са попълнени.')}
        </Muted>
        <Label>{t('Тип')}</Label>
        <View style={{ flexDirection: 'row', backgroundColor: COLORS.bg, borderRadius: 10, padding: 4 }}>
          {(
            [
              ['INDIVIDUAL', t('Физическо лице')],
              ['COMPANY', t('Фирма')],
            ] as const
          ).map(([key, label]) => (
            <Text
              key={key}
              onPress={() => setBType(key)}
              style={{
                flex: 1,
                textAlign: 'center',
                paddingVertical: 10,
                borderRadius: 8,
                overflow: 'hidden',
                fontWeight: bType === key ? '700' : '400',
                color: COLORS.text,
                backgroundColor: bType === key ? COLORS.card : 'transparent',
              }}
            >
              {label}
            </Text>
          ))}
        </View>
        <Label>{bType === 'COMPANY' ? t('Име на фирмата') : t('Име и фамилия')}</Label>
        <Input value={bName} onChangeText={setBName} placeholder={bType === 'COMPANY' ? t('Крео ЕООД') : t('Иван Иванов')} />
        {bType === 'INDIVIDUAL' ? (
          <>
            <Label>{t('ЕГН')}</Label>
            <Input value={bEgn} onChangeText={setBEgn} keyboardType="number-pad" maxLength={10} placeholder="__________" />
            <Muted>{t('ЕГН се пази криптирано и се вижда само от администратора във фактурата.')}</Muted>
          </>
        ) : (
          <>
            <Label>{t('ЕИК')}</Label>
            <Input value={bEik} onChangeText={setBEik} keyboardType="number-pad" placeholder="123456789" />
            <Label>{t('МОЛ')}</Label>
            <Input value={bMol} onChangeText={setBMol} placeholder={t('Иван Иванов')} />
          </>
        )}
        <Label>{t('ДДС № (по избор)')}</Label>
        <Input value={bVat} onChangeText={setBVat} autoCapitalize="characters" placeholder="BG..." />
        <Label>{t('Град')}</Label>
        <Input value={bCity} onChangeText={setBCity} placeholder={t('София')} />
        <Label>{t('Адрес')}</Label>
        <Input value={bAddress} onChangeText={setBAddress} placeholder={t('ул. …, №')} />
        <Button
          title={billingComplete ? t('Запази промените') : t('Запази данните за фактура')}
          onPress={saveBilling}
          disabled={!bName.trim() || !bAddress.trim() || (bType === 'INDIVIDUAL' ? bEgn.trim().length !== 10 : !bEik.trim())}
          loading={loading}
        />
      </Card>

      {/* Верификация с лична карта */}
      <SectionLabel>{t('Верификация')}</SectionLabel>
      <Card>
        {profile.idVerified ? (
          <Badge text={t('Верифициран с лична карта ✓')} color={COLORS.success} />
        ) : profile.idSubmitted ? (
          <>
            <Badge text={t('Документите чакат преглед')} color="#D97706" />
            <Muted>{t('Администратор ще ги провери. Ще получите известие.')}</Muted>
          </>
        ) : !billingComplete ? (
          <Muted>{t('Първо попълнете данните за фактура по-горе — те са задължителни за верификацията.')}</Muted>
        ) : (
          <>
            <Muted>
              {t('Преди първото теглене е нужна еднократна верификация: снимка на личната карта + селфи. Документите са криптирани, вижда ги само администраторът и се изтриват след проверката (GDPR).')}
            </Muted>
            <Button
              title={idCardUri ? t('✓ Лична карта — сменена') : t('1. Снимка на личната карта')}
              variant="secondary"
              onPress={() => pick(setIdCardUri)}
            />
            <Button
              title={selfieUri ? t('✓ Селфи — сменено') : t('2. Селфи')}
              variant="secondary"
              onPress={() => pick(setSelfieUri)}
            />
            <Button title={t('Изпрати за проверка')} onPress={sendVerification} disabled={!idCardUri || !selfieUri} loading={loading} />
          </>
        )}
      </Card>

      {/* Теглене */}
      <SectionLabel>{t('Теглене')}</SectionLabel>
      <Card>
        {profile.balanceCents < settings.minWithdrawCents ? (
          <Muted>
            {t('Тегленето се отключва при {min} € натрупани. Остават {left}.', {
              min: (settings.minWithdrawCents / 100).toFixed(0),
              left: money(settings.minWithdrawCents - profile.balanceCents),
            })}
          </Muted>
        ) : !profile.idVerified ? (
          <Muted>{t('Балансът стига, но първо завършете верификацията с лична карта по-горе.')}</Muted>
        ) : (
          <>
            <Label>{t('Сума (€)')}</Label>
            <Input value={amount} onChangeText={setAmount} keyboardType="decimal-pad" placeholder={(profile.balanceCents / 100).toFixed(2)} />
            <Label>IBAN</Label>
            <Input value={iban} onChangeText={setIban} autoCapitalize="characters" placeholder="BG.." />
            <Muted>{t('Преводът се прави ръчно по банка от администратора — обикновено до няколко работни дни.')}</Muted>
            <Button title={t('Заяви теглене')} onPress={withdraw} disabled={!amount || iban.length < 10} loading={loading} />
          </>
        )}
      </Card>

      {(withdrawals ?? []).length > 0 && (
        <>
          <SectionLabel>{t('Тегления')}</SectionLabel>
          {withdrawals.map((w: any) => (
            <Card key={w.id}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text style={{ fontWeight: '700', color: COLORS.text }}>{money(w.amountCents)}</Text>
                <Badge text={W_STATUS[w.status]?.[0] ?? w.status} color={W_STATUS[w.status]?.[1]} />
              </View>
              <Muted>{new Date(w.createdAt).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG')} · {w.iban}</Muted>
              {w.status === 'REJECTED' && w.note ? <Muted>{t('Причина: {reason}', { reason: w.note })}</Muted> : null}
            </Card>
          ))}
        </>
      )}

      {(invoices ?? []).length > 0 && (
        <>
          <SectionLabel>{t('Моите фактури')}</SectionLabel>
          {invoices.map((inv: any) => (
            <Card key={inv.id}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontWeight: '700', color: COLORS.text }}>
                    {t('Фактура № {number}', { number: String(inv.number).padStart(10, '0') })}
                  </Text>
                  <Muted>{inv.service} · {money(inv.amountCents)} · {new Date(inv.issuedAt).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG')}</Muted>
                </View>
                <Button title="PDF" variant="secondary" onPress={() => Linking.openURL(`${API_URL}/creator-invoice/${inv.id}/pdf?t=${inv.pdfToken}`)} />
              </View>
            </Card>
          ))}
        </>
      )}

      {(ledger ?? []).length > 0 && (
        <>
          <SectionLabel>{t('История на баланса')}</SectionLabel>
          <Card>
            {ledger.map((l: any, i: number) => (
              <View key={l.id}>
                {i > 0 && <Divider />}
                <Row
                  left={l.type === 'WEEK_REWARD' ? t('Одобрена седмица') : l.type === 'WITHDRAWAL_PAID' ? t('Изплатено теглене') : t('Корекция')}
                  right={`${l.amountCents >= 0 ? '+' : ''}${money(l.amountCents)}`}
                />
                <Muted>{new Date(l.createdAt).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG')}</Muted>
              </View>
            ))}
          </Card>
        </>
      )}
    </Screen>
  );
}
